"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PRODUCT_ERROR_CODES = exports.CONSUMER_ERROR_CODES = exports.TICKETS_RECEIVED_PERIOD_DAYS = exports.updateProductSchema = exports.updateConsumerSchema = exports.createConsumerSchema = exports.newProductSchema = exports.authorizedCommittentiSchema = exports.committenteSchema = exports.PRODUCT_PREFIX_PATTERN = exports.CONSUMER_TOKEN_TTL_SECONDS = exports.CLIENT_ID_PREFIX = exports.CONSUMER_EVENT_ACTION_LABELS = exports.CONSUMER_EVENT_ACTIONS = exports.CONSUMER_STATUS_LABELS = exports.CONSUMER_STATUSES = exports.ALLOWED_CONSUMER_ENVIRONMENTS = exports.INSTANCE_ENVIRONMENTS = exports.CONSUMER_ENVIRONMENT_LABELS = exports.CONSUMER_ENVIRONMENTS = void 0;
exports.isEnvironmentAllowed = isEnvironmentAllowed;
const zod_1 = require("zod");
/**
 * Registro dei consumer (PRD-003 US-1, DEC-006, DEC-003): un consumer è un'installazione di un
 * prodotto in un ambiente, con i committenti che può dichiarare e una credenziale OAuth2 client
 * credentials. Contratti condivisi fra API e web; i valori di dominio sono in italiano.
 */
/** Ambiente di un consumer (DEC-006 §4). */
exports.CONSUMER_ENVIRONMENTS = ['produzione', 'collaudo', 'staging'];
exports.CONSUMER_ENVIRONMENT_LABELS = {
    produzione: 'Produzione',
    collaudo: 'Collaudo',
    staging: 'Staging',
};
/** Ambiente di un'istanza EasyHelp (`INSTANCE_ENVIRONMENT`): ne esistono due (glossario «Istanza»). */
exports.INSTANCE_ENVIRONMENTS = ['produzione', 'staging'];
/**
 * Ambienti dei consumer per cui ciascuna istanza emette token (DEC-006 §4, opzione 4-A): la
 * produzione accetta solo consumer di produzione; lo staging di EasyHelp serve sia lo staging sia
 * il collaudo dei prodotti.
 */
exports.ALLOWED_CONSUMER_ENVIRONMENTS = {
    produzione: ['produzione'],
    staging: ['staging', 'collaudo'],
};
/** Vero se l'istanza emette token per i consumer di quell'ambiente. */
function isEnvironmentAllowed(instance, environment) {
    return exports.ALLOWED_CONSUMER_ENVIRONMENTS[instance].includes(environment);
}
/** Stato di un consumer: la revoca è definitiva (DEC-006 §6). */
exports.CONSUMER_STATUSES = ['attivo', 'revocato'];
exports.CONSUMER_STATUS_LABELS = {
    attivo: 'Attivo',
    revocato: 'Revocato',
};
/**
 * Operazioni tracciate sul registro (DEC-006 §5): il token emesso aggiorna solo `lastTokenAt`. Le
 * azioni `webhook_*` sono la notifica di cambio stato (DEC-013, #84).
 */
exports.CONSUMER_EVENT_ACTIONS = [
    'creato',
    'modificato',
    'revocato',
    'webhook_attivato',
    'webhook_modificato',
    'webhook_segreto_rigenerato',
    'webhook_disattivato',
];
exports.CONSUMER_EVENT_ACTION_LABELS = {
    creato: 'Creato',
    modificato: 'Modificato',
    revocato: 'Revocato',
    webhook_attivato: 'Notifica attivata',
    webhook_modificato: 'Indirizzo della notifica modificato',
    webhook_segreto_rigenerato: 'Segreto di firma rigenerato',
    webhook_disattivato: 'Notifica disattivata',
};
/** Prefisso del `client_id` pubblico (`eh_` + 20 caratteri esadecimali). */
exports.CLIENT_ID_PREFIX = 'eh_';
/** Durata del token in secondi (DEC-006 regola 1: 5-15 minuti). */
exports.CONSUMER_TOKEN_TTL_SECONDS = 600;
/** Prefisso del codice ticket (DEC-003): da 2 a 5 lettere maiuscole. */
exports.PRODUCT_PREFIX_PATTERN = /^[A-Z]{2,5}$/;
const productNameSchema = zod_1.z
    .string()
    .trim()
    .min(1, { error: 'Il nome del prodotto è obbligatorio' })
    .max(80, { error: 'Nome del prodotto troppo lungo (massimo 80 caratteri)' });
/** Prefisso normalizzato in maiuscolo: `ec` e `EC` sono lo stesso prefisso. */
const productPrefixSchema = zod_1.z
    .string()
    .trim()
    .toUpperCase()
    .min(1, { error: 'Il prefisso è obbligatorio', abort: true })
    .regex(exports.PRODUCT_PREFIX_PATTERN, { error: 'Il prefisso è di 2-5 lettere (es. EC, ER, CC)' });
const consumerNameSchema = zod_1.z
    .string()
    .trim()
    .min(1, { error: 'Il nome è obbligatorio' })
    .max(120, { error: 'Nome troppo lungo (massimo 120 caratteri)' });
/** Un committente come lo dichiara il prodotto: senza spazi ai bordi, 1-120 caratteri. */
exports.committenteSchema = zod_1.z
    .string()
    .trim()
    .min(1, { error: 'Il committente non può essere vuoto' })
    .max(120, { error: 'Committente troppo lungo (massimo 120 caratteri)' });
/**
 * Committenti autorizzati: come li dichiara il prodotto nel payload (DEC-006 regola 3), senza
 * spazi ai bordi né duplicati; almeno uno.
 */
exports.authorizedCommittentiSchema = zod_1.z
    .array(exports.committenteSchema, { error: 'Indica i committenti autorizzati' })
    .min(1, { error: 'Serve almeno un committente autorizzato' })
    .max(200, { error: 'Troppi committenti (massimo 200)' })
    .transform((values) => [...new Set(values)]);
/** Prodotto nuovo definito insieme al primo consumer (DEC-003). */
exports.newProductSchema = zod_1.z.object({
    name: productNameSchema,
    prefix: productPrefixSchema,
});
/** Corpo di `POST /consumers`: prodotto esistente (`productId`) oppure nuovo (`newProduct`), mai entrambi. */
exports.createConsumerSchema = zod_1.z
    .object({
    name: consumerNameSchema,
    environment: zod_1.z.enum(exports.CONSUMER_ENVIRONMENTS, { error: 'Ambiente non valido' }),
    authorizedCommittenti: exports.authorizedCommittentiSchema,
    productId: zod_1.z.uuid({ error: 'Prodotto non valido' }).optional(),
    newProduct: exports.newProductSchema.optional(),
})
    .refine((value) => Boolean(value.productId) !== Boolean(value.newProduct), {
    error: 'Scegli un prodotto esistente oppure definiscine uno nuovo',
    path: ['productId'],
});
const IMMUTABLE_MESSAGE = 'Prodotto e ambiente non si cambiano dopo la creazione: revoca il consumer e creane uno nuovo';
/** Corpo di `PATCH /consumers/:id`: solo nome e committenti; prodotto e ambiente sono rifiutati. */
exports.updateConsumerSchema = zod_1.z
    .object({
    name: consumerNameSchema.optional(),
    authorizedCommittenti: exports.authorizedCommittentiSchema.optional(),
    productId: zod_1.z.never({ error: IMMUTABLE_MESSAGE }).optional(),
    newProduct: zod_1.z.never({ error: IMMUTABLE_MESSAGE }).optional(),
    environment: zod_1.z.never({ error: IMMUTABLE_MESSAGE }).optional(),
})
    .refine((value) => value.name !== undefined || value.authorizedCommittenti !== undefined, {
    error: 'Nessun campo da aggiornare',
});
/** Corpo di `PATCH /products/:id`: nome e, finché non esistono ticket del prodotto, prefisso. */
exports.updateProductSchema = zod_1.z
    .object({
    name: productNameSchema,
    prefix: productPrefixSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { error: 'Nessun campo da aggiornare' });
/** Periodo del conteggio dei ticket ricevuti mostrato nel registro. */
exports.TICKETS_RECEIVED_PERIOD_DAYS = 30;
/** Codici di errore del registro dei consumer, nel campo `code` del corpo di errore dell'API. */
exports.CONSUMER_ERROR_CODES = {
    /** Il consumer non esiste. */
    notFound: 'consumers.consumer_not_found',
    /** Il consumer è già revocato: la revoca è definitiva. */
    alreadyRevoked: 'consumers.already_revoked',
    /** L'istanza non emette token per consumer di quell'ambiente (DEC-006 §4). */
    environmentNotAllowed: 'consumers.environment_not_allowed',
    /** `CONSUMER_TOKEN_SECRET` assente fuori da sviluppo: l'endpoint token non può firmare. */
    tokenSigningNotConfigured: 'consumers.token_signing_not_configured',
    /** Token mancante, non valido, scaduto, di un altro ambiente o di un consumer revocato. */
    tokenInvalid: 'consumers.token_invalid',
    /** Indirizzo della notifica non ammesso: non `https`, con credenziali, verso una rete privata o non risolvibile (DEC-013 regola 6). */
    webhookUrlNotAllowed: 'consumers.webhook_url_not_allowed',
    /** La notifica di cambio stato è già attiva: si modifica l'indirizzo o si rigenera il segreto. */
    webhookAlreadyActive: 'consumers.webhook_already_active',
    /** La notifica di cambio stato non è attiva per il consumer. */
    webhookNotActive: 'consumers.webhook_not_active',
    /** `CONSUMER_WEBHOOK_ENCRYPTION_KEY` assente fuori da sviluppo: i segreti di firma non si possono custodire. */
    webhookNotConfigured: 'consumers.webhook_not_configured',
};
/** Codici di errore dei prodotti. */
exports.PRODUCT_ERROR_CODES = {
    notFound: 'products.product_not_found',
    /** Nome già usato da un altro prodotto. */
    nameAlreadyUsed: 'products.name_already_used',
    /** Prefisso già usato da un altro prodotto. */
    prefixAlreadyUsed: 'products.prefix_already_used',
    /** Esistono ticket del prodotto: il prefisso non si cambia più (DEC-003). */
    prefixLocked: 'products.prefix_locked',
    /** Esiste già una categoria attiva con quel nome nel set del canale (le maiuscole non contano). */
    categoryExists: 'products.category_exists',
    /** La categoria non esiste o è già stata rimossa. */
    categoryNotFound: 'products.category_not_found',
    /** Il committente non è fra quelli dei consumer del prodotto. */
    committenteUnknown: 'products.committente_unknown',
    /** Esiste già un tipo di riferimento al record con quella chiave nel prodotto (DEC-014). */
    recordReferenceTypeExists: 'products.record_reference_type_exists',
    /** Il tipo di riferimento al record non esiste o è già stato rimosso (DEC-014). */
    recordReferenceTypeNotFound: 'products.record_reference_type_not_found',
};
//# sourceMappingURL=consumers.js.map